import React, { useCallback } from 'react';
import PaginationTable, {
    PaginationTableProps,
} from '../../../../../../ui/DataDisplay/Tables/PaginationTable/PaginationTable';
import { getTrndsClientSongTiktokAudioStats, TrndsClientSongTiktokAudioStats } from '@round/api';
import cn from 'classnames';
import styles from './TopTiktokAudios.module.css';
import Skeleton from '../../../../../../ui/Skeleton/Skeleton';
import { formatNumberToKNotation, roundTo2Dp } from '../../../../../../helpers';
import TruncatedTextCell from '../../../../../../SharedComponents/TableComponents/TruncatedTextCell/TruncatedTextCell';
import BarsSmallChart, {
    ChartValuesEntry,
} from '../../../../../../ui/DataDisplay/Charts/BarsSmallChart/BarsSmallChart';
import { Column } from 'react-table';
import TiktokAudioBadge from '../../../../../TikTok/audios/components/TiktokAudioBadge/TiktokAudioBadge';

export type TopTiktokAudiosTableRow = TrndsClientSongTiktokAudioStats & {
    image_url: string;
    audioUrl: string;
    dailyGrowth: ChartValuesEntry[];
};

type Ordering = Parameters<typeof getTrndsClientSongTiktokAudioStats>[0]['ordering'];

type TopTiktokAudiosTableProps = Pick<
    PaginationTableProps<TopTiktokAudiosTableRow>,
    'loading' | 'data' | 'page' | 'pageSize' | 'setPageSize' | 'setPage' | 'count' | 'onPaginationChange' | 'onRowClick'
> & { order: Ordering };

const TopTiktokAudiosTable = ({
    data,
    loading,
    page,
    setPage,
    pageSize,
    setPageSize,
    count,
    order,
    onPaginationChange,
    onRowClick,
}: TopTiktokAudiosTableProps) => {
    const formatVideoCountBadgeValue = useCallback(
        (n: number) => {
            if (order?.includes('relative')) {
                return `${roundTo2Dp(n * 100)}%`;
            }

            return formatNumberToKNotation(n);
        },
        [order]
    );

    const columns: Column<TopTiktokAudiosTableRow>[] = [
        {
            Header: 'Sound',
            accessor: 'title',
            Cell: ({ row: { original } }) => (
                <TiktokAudioBadge
                    loading={loading}
                    audio={{
                        tiktokId: original.tiktok_id,
                        audioPlayUrl: original.audioUrl,
                        title: original.title,
                        imageUrl: original.image_url,
                        isOriginal: original.is_original,
                    }}
                />
            ),
        },
        {
            Header: 'Author',
            accessor: 'author_name',
            Cell: ({ value }) =>
                loading ? (
                    <Skeleton width="75%" />
                ) : (
                    <TruncatedTextCell className={styles.authorName}>{value}</TruncatedTextCell>
                ),
        },
        {
            Header: 'Video',
            accessor: 'video_count',
            Cell: ({ value, row: { original } }) => {
                if (loading) {
                    return <Skeleton width="75%" />;
                }

                const badgeValue =
                    order && order !== 'video_count'
                        ? Number(original[order.slice(1) as keyof TrndsClientSongTiktokAudioStats])
                        : NaN;

                return (
                    <>
                        <span className={styles.videoCount}>{formatNumberToKNotation(value)}</span>
                        {!isNaN(badgeValue) && (
                            <span
                                className={cn(styles.growthBadge, {
                                    [styles.increasing]: badgeValue >= 0,
                                    [styles.decreasing]: badgeValue < 0,
                                })}
                            >
                                {badgeValue > 0 && '+'}
                                {formatVideoCountBadgeValue(badgeValue)}
                            </span>
                        )}
                    </>
                );
            },
        },
        {
            Header: 'Growth',
            accessor: 'dailyGrowth',
            Cell: ({ value }) => {
                if (loading) {
                    return <Skeleton />;
                }

                return (
                    <div className={styles.dailyGrowth}>
                        <BarsSmallChart
                            values={value.slice(-7)}
                            color="linear-gradient(180deg, #93AAFF 0%, #6BC1FF 100%)"
                        />
                    </div>
                );
            },
        },
    ];
    return (
        <PaginationTable
            loading={loading}
            data={data}
            columns={columns}
            count={count}
            page={page}
            setPage={setPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            onPaginationChange={onPaginationChange}
            noDataLabel="No data available"
            onRowClick={onRowClick}
        />
    );
};

export default TopTiktokAudiosTable;
